<template>
  <tr v-if="localData" :class="{ shadow: localData.role === 'Shadow' }">
    <td
      v-for="(header, index) in headers.filter((h) => !h.hidden)"
      :key="index"
    >
      <template v-if="header.value === 'estimation.description'">
        <v-textarea
          id="project-description-input"
          v-model="localData.description"
          data-cy="project-description-input"
          class="secondary-background"
          label="Description"
          hide-details
          required
          outlined
          dense
          :rules="[(bs) => !!bs?.trim() || 'Description is required']"
        />
      </template>
      <template v-else-if="header.value === 'estimation.taskTypeTitle'">
        <v-text-field
          id="task-type-input"
          v-model="taskTypeTitle"
          class="secondary-background"
          label="Task"
          outlined
          dense
          hide-details
          :disabled="isLocked"
          :rules="[(v) => !!v || 'Task is required']"
        />
      </template>
      <template v-else-if="header.value === 'estimation.roleTypeTitle'">
        <v-select
          id="role-input"
          v-model="localData.role_id"
          :menu-props="{ offsetY: true }"
          :items="roleItems"
          item-text="name"
          item-value="id"
          class="secondary-background"
          label="Role"
          :rules="[(bs) => !!bs?.trim() || 'Role is required']"
          hide-details
          required
          outlined
          dense
        />
      </template>
      <template v-else-if="header.value === 'estimation.estimate'">
        <v-text-field
          id="project-estimate-input"
          v-model="localData.estimate"
          data-cy="project-estimate-input"
          class="secondary-background"
          label="Estimate"
          type="number"
          hide-details
          required
          outlined
          dense
          :rules="[(bs) => (bs >= 0) || 'Estimate is required']"
        />
      </template>
    </td>
    <td>
      <v-row>
        <v-btn
          text
          icon
          small
          @click.stop="updateEstimation()"
        >
          <v-icon>mdi-check</v-icon>
        </v-btn>
        <v-btn
          text
          icon
          small
          @click.stop="cancelEdit()"
        >
          <v-icon>mdi-cancel</v-icon>
        </v-btn>
      </v-row>
    </td>
  </tr>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';

import { projectStatusFilterValues } from '@/constants/projectStatuses';
import { projectTypes } from '@/constants/projectTypes';
import { NotificationHelper } from '@/helpers/notification.helper';

export default {
  name: 'ProjectEstimationInput',
  components: {
  },
  props: {
    estimation: {
      type: Object,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    taskTypes: {
      type: Array,
      required: true,
    },
    roleItems: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      endDateMenu: false,
      startDateMenu: false,
      localData: null,
      projectStatusFilterValues,
      projectTypes,
      searchValue: '',
    };
  },
  computed: {
    ...mapGetters('user/settings', ['denseTables', 'dateFormatSettings']),
    ...mapGetters('projects/main', ['project', 'search']),
    ...mapGetters('shared/taskTypes', [
      'allTaskTypes',
    ]),
    isLocked() {
      return (
        this.localData?.task_type &&
        this.localData.task_type.owner_project_id !== this.project.id
      );
    },

    taskTypeTitle: {
      get() {
        return this.localData?.task_type?.title || '';
      },
      set(val) {
        if (!this.localData?.task_type) {
          this.localData.task_type = {
            title: val,
            owner_project_id: this.project.id,
          };

          return;
        }

        if (!this.isLocked) {
          this.localData.task_type.title = val;
        }
      },
    },
  },
  created() {
    this.getAllTaskTypes();
  },
  async mounted() {
    if (!this.allTaskTypes || this.allTaskTypes.length === 0) {
      await this.getAllTaskTypes();
    }

    this.localData = _.cloneDeep(this.estimation);

    if (this.localData.task_type && this.allTaskTypes && this.allTaskTypes.length > 0) {
      const taskTypeObject = this.allTaskTypes.find(taskType => taskType.id === this.localData.task_type);
      if (taskTypeObject) {
        this.localData.task_type = taskTypeObject;
      }
    }
  },
  methods: {
    ...mapActions('shared/taskTypes', ['getAllTaskTypes', 'addTaskType', 'createTaskTypeWithoutProject', 'addTaskTypeToProject']),

    cancelEdit() {
      this.$emit('cancel');
    },
    validate() {
      if (this.localData.estimate < 0) {
        NotificationHelper.showError('Estimation cannot be negative');

        return false;
      }

      return true;
    },
    async updateEstimation() {
      if (!this.validate()) {
        return;
      }

      const title = this.searchValue || this.localData?.task_type?.title;

      const estimationData = {
        ...this.localData,
        task_type: this.localData.task_type?.id || this.localData.task_type,
        taskTypeTitle: title,
      };

      this.$emit('save', estimationData);

      if (this.localData.task_type?.id) {
        this.addTaskTypeToProject({
          projectId: this.project.id,
          taskTypeId: this.localData.task_type.id,
        });
      }

      await this.createTaskType({
        title,
        owner_project_id: this.project.id,
      });
    },
  },
};
</script>

<style lang="less">
@import "~variables";

.row-editor {
  .project-input {
    max-width: 270px;
  }

  .v-label {
    font-size: @table-font-size;
  }

  .v-text-field__slot {
    font-size: @table-font-size;
  }

  .v-text-field {
    input {
      font-size: @table-font-size;
    }
  }

  .clipped div.v-select__selection {
    overflow: hidden;
    white-space: nowrap;
  }

  // Validation error message
  .v-input {
    .v-text-field__details {
      overflow: visible;
    }

    .v-messages__message {
      position: absolute;
      background: @soft-peach;
      border: 1px solid @pink;
      border-radius: 0 2px 2px 2px;
      width: max-content;
      max-width: 120px;
      padding: 3px;
      color: @red-dark;
      box-shadow: 0 -1px 8px 2px @pink-light;

      &::before {
        content: "";
        position: absolute;
        border: solid transparent;
        border-width: 0 6px 5px 0;
        border-bottom-color: @pink;
        top: -5px;
        left: -1px;
      }
    }
  }
  .time__cell {
    position: relative;
  }
}
</style>
